import { useEffect, useState } from 'react';

/**
 * @deprecated This hook creates a shared AbortController which can cause unintended cancellations.
 * Instead, create AbortController instances inside each useEffect that needs them:
 *
 * @example
 * ```tsx
 * // ✅ Correct pattern - each effect manages its own AbortController
 * useEffect(() => {
 *   const abortController = new AbortController();
 *   const { signal } = abortController;
 *
 *   fetchData(signal).then(setData);
 *
 *   return () => abortController.abort();
 * }, []);
 * ```
 *
 * Hook that creates and manages a single AbortController instance shared across the component.
 * The AbortController is automatically aborted when the component unmounts.
 *
 * WARNING: Using a shared AbortController can lead to race conditions where one effect
 * cancels requests from another effect. Each effect should manage its own AbortController.
 *
 * @returns An object containing the abortController instance
 */
const useAbortController = () => {
  const [abortController] = useState(new AbortController());
  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  return { abortController };
};

export default useAbortController;